import React, { useState } from 'react';
import { admin } from '../../services/api';
import '../../styles/AdminQueueModals.css';

function AddMedicationModal({ show, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    setid: '',
    medication_name: '',
    category: '',
    drug_class: '',
    priority: '',
    notes: '',
    mark_urgent: false,
  });
  const [checking, setChecking] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validatingSetId, setValidatingSetId] = useState(false);
  const [setIdValid, setSetIdValid] = useState(null); // null = not checked, true = valid, false = invalid

  const categories = [
    'High-Volume',
    'Specialty',
    'New Brand',
    'Generic',
    'OTC',
  ];

  const priorityMap = {
    'High-Volume': 'HIGH',
    'Specialty': 'HIGH',
    'New Brand': 'MEDIUM-HIGH',
    'Generic': 'MEDIUM',
    'OTC': 'LOW',
  };

  const handleSetIdBlur = async () => {
    if (!formData.setid || formData.setid.length < 3) return;
    
    setChecking(true);
    setDuplicate(null);
    setValidatingSetId(true);
    setSetIdValid(null);
    
    try {
      // 1. Check for duplicates in our database
      const dupResponse = await admin.checkDuplicate(formData.setid);
      if (dupResponse.data.exists) {
        setDuplicate(dupResponse.data);
      }
      
      // 2. Validate SetID with DailyMed API
      try {
        const dailymedResponse = await fetch(
          `https://dailymed.nlm.nih.gov/dailymed/services/v2/spls/${formData.setid}.json`
        );
        
        if (dailymedResponse.status === 404) {
          setSetIdValid(false);
          alert("❌ SetID not found on DailyMed. Please verify and try again.");
        } else if (dailymedResponse.ok) {
          const data = await dailymedResponse.json();
          setSetIdValid(true);
          
          // Auto-fill medication name from DailyMed if not already filled
          if (!formData.medication_name && data.data && data.data[0]?.title) {
            setFormData(prev => ({
              ...prev,
              medication_name: data.data[0].title
            }));
          }
        } else {
          // Non-404 error, allow submission (DailyMed might be down)
          setSetIdValid(null);
          console.warn('DailyMed API returned non-200/404 status:', dailymedResponse.status);
        }
      } catch (dailymedError) {
        // Network error, allow submission (don't block on DailyMed downtime)
        console.error('DailyMed validation error:', dailymedError);
        setSetIdValid(null);
      }
      
    } catch (err) {
      console.error('Duplicate check failed:', err);
    } finally {
      setChecking(false);
      setValidatingSetId(false);
    }
  };

  const handleCategoryChange = (category) => {
    setFormData({
      ...formData,
      category,
      priority: priorityMap[category] || 'MEDIUM',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (duplicate && !window.confirm(`This medication already exists in ${duplicate.location}. Add anyway?`)) {
      return;
    }

    setSubmitting(true);
    
    try {
      const payload = {
        setid: formData.setid.trim(),
        medication_name: formData.medication_name.trim(),
        category: formData.category,
        drug_class: formData.drug_class.trim() || undefined,
        priority: formData.mark_urgent ? 'URGENT' : formData.priority,
        notes: formData.notes.trim() || undefined,
      };

      await admin.addToQueue(payload);
      
      alert('Medication added to queue successfully!');
      
      // Reset form
      setFormData({
        setid: '',
        medication_name: '',
        category: '',
        drug_class: '',
        priority: '',
        notes: '',
        mark_urgent: false,
      });
      setDuplicate(null);
      setSetIdValid(null);
      setValidatingSetId(false);
      
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add medication:', err);
      alert('Failed to add medication: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        setid: '',
        medication_name: '',
        category: '',
        drug_class: '',
        priority: '',
        notes: '',
        mark_urgent: false,
      });
      setDuplicate(null);
      setSetIdValid(null);
      setValidatingSetId(false);
      setChecking(false);
      onClose();
    }
  };

  if (!show) return null;

  const displayPriority = formData.mark_urgent ? 'URGENT' : formData.priority;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content queue-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Add Medication to Queue</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>SetID *</label>
            <input
              type="text"
              required
              value={formData.setid}
              onChange={(e) => {
                setFormData({...formData, setid: e.target.value});
                setSetIdValid(null); // Reset validation when user types
              }}
              onBlur={handleSetIdBlur}
              placeholder="abc-123-def-456"
              disabled={submitting}
              className={setIdValid === false ? 'input-error' : setIdValid === true ? 'input-success' : ''}
            />
            {(checking || validatingSetId) && (
              <small className="text-info">
                {validatingSetId ? '🔄 Validating SetID with DailyMed...' : 'Checking for duplicates...'}
              </small>
            )}
            {setIdValid === true && !validatingSetId && (
              <small className="text-success">✅ SetID verified on DailyMed</small>
            )}
            {setIdValid === false && !validatingSetId && (
              <div className="validation-error">
                ❌ SetID not found on DailyMed - please verify
              </div>
            )}
            {duplicate && (
              <div className="duplicate-warning">
                ⚠️ Already exists in <strong>{duplicate.location}</strong>: {duplicate.name || 'Unknown'}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label>Medication Name *</label>
            <input
              type="text"
              required
              value={formData.medication_name}
              onChange={(e) => setFormData({...formData, medication_name: e.target.value})}
              placeholder="Ibrance"
              disabled={submitting}
            />
          </div>
          
          <div className="form-group">
            <label>Category *</label>
            <select
              required
              value={formData.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Drug Class (optional)</label>
            <input
              type="text"
              value={formData.drug_class}
              onChange={(e) => setFormData({...formData, drug_class: e.target.value})}
              placeholder="e.g., CDK4/6 Inhibitor"
              disabled={submitting}
            />
          </div>
          
          <div className="form-group">
            <label>Priority (auto-assigned)</label>
            <div className={`priority-display priority-${displayPriority?.toLowerCase()}`}>
              {displayPriority || 'Select a category first'}
            </div>
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.mark_urgent}
                onChange={(e) => setFormData({...formData, mark_urgent: e.target.checked})}
                disabled={submitting}
              />
              <span>Mark as Urgent</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes or context..."
              rows="3"
              disabled={submitting}
            />
          </div>
          
          <div className="modal-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting || checking}
            >
              {submitting ? 'Adding...' : 'Add to Queue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddMedicationModal;
